
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function PrivacyPolicy() {
  const sections = [
    {
      number: '1',
      title: 'What Does MandaPost Do?',
      content: `If you have any questions or concerns about our use of your information, please contact us using the contact details provided in the "How to Contact Us" section of this Privacy Policy.

MandaPost is a provider of AI-powered social media management software, including content creation, scheduling, analytics, and team collaboration tools. We are operated by Osem Dynamics LLC and headquartered in the United States. For more information about MandaPost, please visit our website.`,
    },
    {
      number: '2',
      title: 'Information Collected',
      content: `We collect information about visitors to our Site, our customers and their users of the Service, users of our customers' social media pages/properties, and job applicants. Such information is collected from the following sources:

**Information We Collect Directly from You:** The type of information that we collect directly from you varies based on your interaction with our Site and our Service. For example, we collect information directly from you when you register an account with us, contact us with a customer service issue, apply for a job, and complete forms on the Site and/or Service. We also collect information that you send us via any medium, including, but not limited to email address and phone number. In addition, we collect information about you from your social media interactions on third-party social media properties.

**Information We Receive from Customers:** We receive information from customers in order to provide our Service. Such information may include a user's social media handle, username, profile picture, biography, follower counts, website URL, first and last name (if provided), and messages or communications with our customers. We collect information from individuals employed by our customers such as their contact information in order to provide our Service.`,
    },
    {
      number: '3',
      title: 'Information Collected Automatically',
      content: `When you visit our Site or use our Service, we may collect certain information automatically from your device. This information may be considered personal information under applicable data protection laws.

Specifically, the information we collect automatically may include information like your IP address, device type, unique device identification numbers, browser type, broad geographic location (e.g., country or city-level location), third-party webpages accessed via the Service, and other technical information. We may also collect information about how your device has interacted with our Site (including the pages accessed and links clicked) or Service (including content accessed).

Collecting this information enables us to better understand the visitors who come to our Site or use our Service, where they come from, and what content and functionality is of interest to them. We use this information for our internal analytics purposes and to improve the quality and relevance of our Site and/or Service to our visitors and users.

Some of this information may be collected using cookies and similar tracking technology, as explained further under the "Cookies and Similar Tracking Technology" section below.`,
    },
    {
      number: '4',
      title: 'Information from Other Sources',
      content: `We also collect information from other sources. The following are the categories of sources we collect information from:

• Data brokers or resellers from which we purchase data to verify and supplement the data we collect
• Social networks when you engage with our content, reference our Site or Service, or grant us permission to access information from the social networks
• Partners that offer co-branded services, sell or distribute our products, or engage in joint marketing activities
• Customers that provide us with information which we process as a service provider
• Third-party payment processors (such as Stripe) for payment processing`,
    },
    {
      number: '5',
      title: 'Do-Not-Track',
      content: `Your browser settings may allow you to automatically transmit a "Do Not Track" signal to online services you visit. Note, however, there is no industry consensus as to what site and app operators should do with regard to these signals.

Accordingly, unless and until the law is interpreted to require us to do so, our systems do not recognize browser "do-not-track" requests. You may, however, disable certain tracking as discussed in this Privacy Policy (e.g., by disabling cookies, or using private browsing modes).`,
    },
    {
      number: '6',
      title: 'Use of Information',
      content: `MandaPost processes information for business and commercial purposes in accordance with the practices described in this Privacy Policy. Our business purposes for collecting and using information include the following:

• Operate and improve the Site and Service
• Provide the Service and other products and services that may be requested or could be of interest
• Facilitate subscription processing, reviews, and analysis
• Evaluate user interest and needs in order to improve the Service and make available other offers, products, or services
• Record calls for quality, training, and Service improvement purposes (upon receipt of consent where required by applicable law)
• Monitor use of the Service, including for troubleshooting and product improvement purposes
• Provide customer support
• Communicate and provide additional information that may be of interest to users through email or other means
• Conduct online research surveys regarding the Site and/or Service
• Send you reminders, technical notices, updates, product announcements, security alerts, and support and administrative messages
• Provide advertisements to you through email messages
• As necessary to consider your job application for open positions
• Manage our everyday business needs such as Site and/or Service administration, analytics, fraud prevention, enforcement of our legal terms, and any other contractual agreement relating to our Service or to comply with the law
• Fulfill any other business or commercial purposes at your direction or with your notice and/or consent

Notwithstanding the above, we may use information that does not identify you (including information that has been aggregated or de-identified) for any purpose except as prohibited by applicable law.`,
    },
    {
      number: '7',
      title: 'Categories of Third Parties With Whom We May Share Your Information',
      content: `We share information we collect in accordance with the practices described in this Privacy Policy. The following are the categories of recipients:

• To our affiliates, service providers, and partners who provide data processing services to us or who otherwise process information for purposes described in this Privacy Policy
• To our customers in order to effectively provide our social media management, analytics, and content creation services
• To any competent law enforcement body, regulatory body, government agency, court, or other third party where we believe disclosure is necessary (i) as a matter of applicable law or court order, (ii) to exercise, establish, or defend our legal rights, or (iii) to protect your vital interests or those of any other person
• To a potential buyer (and its agents, investors, and/or advisors) in connection with any proposed or actual purchase, merger, or acquisition of any part of our business
• To vendors for business and commercial purposes, including analytics and advertising technology companies
• To any other person with notice to you and your consent to the disclosure
• When you make information public through the Site or Service, your information is publicly viewable

**Third-Party Services:** Please note the Service uses third-party services, and if you use these third-party services through the Service, you are subject to the privacy policies of these third-party services, including but not limited to:

• X (Twitter) Privacy Policy
• Meta (Facebook) Privacy Policy
• Instagram Data Policy
• LinkedIn Privacy Policy
• TikTok Privacy Policy
• Pinterest Privacy Policy
• YouTube Privacy Policy
• Google Privacy Policy

Notwithstanding the above, we may share information that does not identify you (including information that has been aggregated or de-identified) except as prohibited by applicable law.`,
    },
    {
      number: '8',
      title: 'Data Retention',
      content: `We retain information we collect from you where we have an ongoing legitimate business need to do so (for example, to provide you with a service you have requested or to comply with applicable legal, tax, or accounting requirements).

When we have no ongoing legitimate business need to process your information, we will either delete or anonymize it in accordance with our data retention policy, or, in the limited circumstances where this is not possible (for example, because your information has been stored in backup archives), then we will securely store your information and isolate it from any further processing until deletion is possible.`,
    },
    {
      number: '9',
      title: 'Sensitive Personal Information',
      content: `The terms that govern your use of the Service prohibit you from providing MandaPost with sensitive personal information as that term is defined under applicable law.

We do not knowingly collect or process sensitive personal information such as health data, financial account information, social security numbers, or other similarly protected information unless explicitly required for specific service features with your consent.`,
    },
    {
      number: '10',
      title: 'Legal Basis for Processing Personal Data (EEA or UK Visitors/Users Only)',
      content: `If you are a visitor or user from the European Economic Area or the UK, our legal basis for collecting and using the personal data described above will depend on the personal data concerned and the specific context in which we collect it.

However, we will normally collect personal data from you only where we have your consent to do so, where we need the personal data to perform a contract with you, or where the processing is in our legitimate interests and not overridden by your data protection interests or fundamental rights and freedoms. In some cases, we may also have a legal obligation to collect personal data from you.

If we ask you to provide personal data to comply with a legal requirement or to perform a contract with you, we will make this clear at the relevant time and advise you whether the provision of your personal data is mandatory or not (as well as of the possible consequences if you do not provide your personal data).

Similarly, if we collect and use your personal data in reliance on our legitimate interests (or those of any third party), we will make clear to you at the relevant time what those legitimate interests are.

If you have questions about or need further information concerning the legal basis on which we collect and use your personal data, please contact us using the contact details provided under the "How to Contact Us" section below.`,
    },
    {
      number: '11',
      title: 'Cookies and Similar Tracking Technology',
      content: `We use cookies and similar tracking technology (collectively, "Cookies") to collect and use information about you, including to serve interest-based advertising.

Cookies are small text files that are placed on your device when you visit our Site. They help us recognize your device, remember your preferences, and provide you with a better user experience. We use both session cookies (which expire when you close your browser) and persistent cookies (which stay on your device until you delete them or they expire).

**Types of Cookies We Use:**

• **Essential Cookies:** Necessary for the Site to function properly
• **Analytics Cookies:** Help us understand how visitors use our Site
• **Functional Cookies:** Remember your preferences and settings
• **Advertising Cookies:** Used to deliver relevant advertisements

We also use analytics services, such as Google Analytics, to help us understand how users access and use the Service. Google provides tools to allow you to opt out of the use of certain information collected by Google Analytics at https://tools.google.com/dlpage/gaoptout.

**Managing Cookies:** You can control and manage cookies through your browser settings. However, please note that disabling certain cookies may affect the functionality of our Site and Service.`,
    },
    {
      number: '12',
      title: 'Social Media and Technology Integrations',
      content: `We offer parts of our Service through websites, platforms, and services operated or controlled by separate entities. In addition, we integrate technologies operated or controlled by separate entities into parts of our Site and Service. Some examples include:

**Links:** Our Site and Service include links that hyperlink to websites, platforms, and other services not operated or controlled by us.

**Liking, Sharing, and Logging-In:** We may embed a pixel or SDK on our Site or Service that allows you to "like" or "share" content on, or log in to your account through social media. If you choose to engage with such integration, we may receive information from the social network that you have authorized to share with us.

**Brand Pages:** We may offer our content through social media. Any information you provide to us when you engage with our content is treated in accordance with this Privacy Policy.

Please note that when you interact with other entities, including when you leave our Site or Service, those entities may independently collect information about you and solicit information from you. The information collected and stored by those entities remains subject to their own policies and practices.`,
    },
    {
      number: '13',
      title: 'Your Data Protection Rights',
      content: `You have the following data protection rights:

• **Access, Correct, Update, or Delete:** You can request to access, correct, update, or delete your information at any time by contacting us using the contact details provided below.

• **Object to Processing:** If you are a resident of the European Economic Area or the United Kingdom, you can object to processing of your personal data, ask us to restrict processing, or request portability of your personal data.

• **Opt-Out of Marketing:** You have the right to opt out of marketing communications we send you at any time. You can exercise this right by clicking on the "unsubscribe" or "opt-out" link in the marketing emails we send you.

• **Withdraw Consent:** If we have collected and processed your information with your consent, you can withdraw your consent at any time. Withdrawing your consent will not affect the lawfulness of any processing we conducted prior to your withdrawal.

• **Complain to Data Protection Authority:** You have the right to complain to a data protection authority about our collection and use of your information.

We respond to all requests we receive from individuals wishing to exercise their data protection rights in accordance with applicable data protection laws.`,
    },
    {
      number: '14',
      title: 'Security & User ID/Password',
      content: `Our Site and/or Service implement and maintain various reasonable and appropriate administrative, physical, and technical security safeguards to help protect information about you from loss, theft, misuse, and unauthorized access, disclosure, alteration, and destruction.

These security safeguards include, but are not limited to:

• Network and host security controls (e.g., firewalls, intrusion detection systems)
• Data encryption (both at rest and during transmission)
• Operating procedures designed to protect your information
• Regular security assessments and updates

You should protect your user ID and password and NOT share it with anyone. Additionally, we recommend enabling two-factor authentication where available.

If you believe your user ID and password have been compromised or you have trouble changing your credentials, please contact our technical support department at support@mandapost.com.

Nevertheless, transmission via the internet is not completely secure, and we cannot guarantee the security of information about you.`,
    },
    {
      number: '15',
      title: 'CAN-SPAM Compliance Notice (U.S. Users Only)',
      content: `We may send periodic promotional or informational emails to you. You may opt out of promotional communications by following the unsubscribe or opt-out instructions contained in the email.

Please note that it may take up to 10 business days for us to process opt-out requests. If you opt out of receiving promotional emails about recommendations or other information we think may interest you, we may still send you emails about your account or any Service you have requested or received from us.`,
    },
    {
      number: '16',
      title: 'International Transfers',
      content: `Your information may be transferred to, and processed in, countries other than the country in which you are resident. These countries may have data protection laws that are different from the laws of your country.

Specifically, our servers are located in the United States, and our service providers and partners operate around the world. This means that when we collect your information, we may process it in any of these countries.

However, we have taken appropriate safeguards to ensure that your information will remain protected in accordance with this Privacy Policy and applicable legal requirements. These include our use of Standard Contractual Clauses approved by the European Commission (along with the UK Addendum, where appropriate) and other legally recognized transfer mechanisms.`,
    },
    {
      number: '17',
      title: 'Transfers Outside the EEA, United Kingdom, and Switzerland',
      content: `Osem Dynamics LLC is committed to protecting your personal data when transferring it outside of the European Economic Area (EEA), United Kingdom, or Switzerland.

We comply with applicable data protection frameworks and use Standard Contractual Clauses and other appropriate safeguards to ensure that your personal data receives an adequate level of protection when transferred internationally.

For transfers from the EEA, UK, or Switzerland to the United States, we rely on approved transfer mechanisms such as Standard Contractual Clauses and adequacy decisions where available.`,
    },
    {
      number: '18',
      title: 'Contacting Us, Complaints and Dispute Resolution',
      content: `MandaPost commits to resolve complaints about your privacy and our collection or use of your information. Individuals with privacy inquiries or complaints should first contact us by email at privacy@mandapost.com.

We will work to resolve your issue and will respond within 45 days of receipt. If, however, you believe that we have not been able to assist with your complaint or concern, and you are located in the EEA, the United Kingdom, or Switzerland, you have the right to lodge a complaint with the competent supervisory authority.

In certain situations, we may be required to disclose personal data in response to lawful requests by public authorities, including to meet national security or law enforcement requirements.`,
    },
    {
      number: '19',
      title: 'Privacy Complaints in Brazil',
      content: `In circumstances in which Brazil law is controlling, we commit to resolve complaints about your privacy and our collection or use of your information.

Brazilian residents may contact us at privacy@mandapost.com to submit privacy complaints or inquiries. We have further committed to refer unresolved privacy complaints to an independent dispute resolution mechanism in accordance with Brazilian data protection law (LGPD).`,
    },
    {
      number: '20',
      title: 'Children',
      content: `This Service is intended for a general audience and is not directed at children under thirteen (13) years of age.

Consistent with the federal Children's Online Privacy Protection Act of 1998 (COPPA), we do not knowingly request personal information from anyone under the age of 13 without requiring parental consent. Any person who provides their personal information to us through our Site or Service represents that they are at least 13 years of age.

If you are a parent or guardian and you believe we have collected information from your child in a manner not permitted by law, contact us at privacy@mandapost.com. We will remove the data to the extent required by applicable laws.

We do not knowingly "sell" or "share," as those terms are defined under the California Consumer Privacy Act, the personal information of minors under 16 years old who are California residents.`,
    },
    {
      number: '21',
      title: 'Your Nevada Privacy Rights',
      content: `Nevada law (SB 220) permits customers in Nevada to opt out of the sale of certain kinds of personal information. A sale under Nevada law is the transfer of this personal information to third parties for monetary consideration.

MandaPost does not sell your personal information to third parties as defined in Nevada law. If you are a Nevada resident and wish to obtain information about our compliance with Nevada law, please contact us at privacy@mandapost.com.`,
    },
    {
      number: '22',
      title: 'Updates to this Privacy Policy',
      content: `We may update this Privacy Policy from time to time in response to changing legal, technical, or business developments. When we update our Privacy Policy, we will take appropriate measures to inform you, consistent with the significance of the changes we make.

We will obtain your consent to any material Privacy Policy changes if and where this is required by applicable data protection laws.

You can see when this Privacy Policy was last updated by checking the "last updated" date displayed at the top of this Privacy Policy.`,
    },
    {
      number: '23',
      title: 'How to Contact Us',
      content: `If you have any questions about this Privacy Policy or our privacy practices, please contact us at:

**Email:** privacy@mandapost.com
**Support:** support@mandapost.com
**Legal Inquiries:** legal@mandapost.com

**Mailing Address:**
Osem Dynamics LLC
[Your Business Address]

If you have a disability and would like to access this Privacy Policy in an alternative format, please contact us at privacy@mandapost.com.

When you contact us, please indicate in which country and/or state you reside.`,
    },
    {
      number: '24',
      title: 'Additional Disclosures for California, Colorado, and Virginia Residents',
      content: `Unless otherwise required by applicable law, the following terms shall have the following meaning:

**"Targeted advertising"** or **"Cross-context behavioral advertising"** means displaying advertisements to a consumer where the advertisement is selected based on personal data obtained from that consumer's activities over time and across nonaffiliated websites or online applications to predict such consumer's preferences or interests.

**"Sale"** means renting, releasing, disclosing, disseminating, making available, transferring, or otherwise communicating a consumer's personal information by the business to a third party for monetary or other valuable consideration.

**"Profiling"** means any form of automated processing of personal information to evaluate certain personal aspects relating to a natural person.

**Notice of Collection:** In the 12 months prior to the last update of this Privacy Policy, we have collected the following categories of personal information:

• Identifiers (name, email address, phone number, IP address, account ID)
• Customer records (billing and shipping address, payment information)
• Commercial information (purchases and engagement with the Service)
• Internet activity (interactions with our Service)
• Audio or visual data (pictures or videos you post on our Service)
• Employment and education data (for job applicants)
• Inferences (information about your interests, preferences, and favorites)

**Right to Know and Delete:** California, Colorado, and Virginia residents have the right to request that we delete the personal information we have collected about them (subject to certain exemptions) and the right to know certain information about our data practices in the preceding 12 months.

To exercise any of these rights, please email us at privacy@mandapost.com or call our toll-free number at (866) 878-3231. We will confirm receipt of your request within 10 days.

**Right to Correct:** You have the right to request that we correct inaccurate personal information that we have collected about you.

**Right to Opt-Out:** You have the right to opt out of processing of your personal information for the purpose of (i) targeted advertising, (ii) "sale" or "sharing" of personal information, or (iii) profiling to provide you with tailored content.

**Right to Non-Discrimination:** You have the right not to receive discriminatory treatment by us for the exercise of any of your rights.

**Authorized Agent:** You can designate an authorized agent to submit requests on your behalf. However, we will require written proof of the agent's permission to do so and verify your identity directly.`,
    },
    {
      number: '25',
      title: 'California Shine the Light',
      content: `Pursuant to Section 1798.83 of the California Civil Code, residents of California can obtain certain information about the types of personal information that companies with whom they have an established business relationship have shared with third parties for direct marketing purposes during the preceding calendar year.

In particular, the law provides that companies must inform consumers about the categories of personal information that have been shared with third parties, the names and addresses of those third parties, and examples of the types of services or products marketed by those third parties.

To request a copy of the information disclosure provided by MandaPost pursuant to Section 1798.83 of the California Civil Code, please contact us via email at privacy@mandapost.com.`,
    },
    {
      number: '26',
      title: 'Virginia and Colorado Residents',
      content: `If you make a request to exercise any of the above data access rights and we are unable to comply with your request, you may request to appeal our decision.

To appeal any data privacy request decision, please contact us by emailing privacy@mandapost.com with the subject line "Data Access Request Appeal."

If after you complete the appeal process with us, you are still not satisfied with our response, you may contact your Attorney General to file a complaint.

**Virginia residents:**
Office of the Attorney General
202 North 9th Street
Richmond, Virginia 23219
Phone: (804) 786-2071
Website: https://www.oag.state.va.us/

**Colorado residents:**
Office of the Attorney General
Colorado Department of Law
Ralph L. Carr Judicial Building
1300 Broadway, 10th Floor
Denver, CO 80203
Phone: (720) 508-6000
Website: https://coag.gov/`,
    },
  ];

  return (
    <div className="min-h-screen bg-white font-['Montserrat']">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#e4f4ff]/20 via-white to-[#b3d9ff]/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center px-4 py-2 bg-[#e4f4ff]/30 rounded-full text-[#22577A] font-medium mb-6">
            <i className="ri-shield-check-line mr-2"></i>
            Privacy & Data Protection
          </div>

          <h1 className="text-5xl lg:text-6xl font-bold text-[#22577A] mb-6">
            Privacy Policy
          </h1>

          <div className="flex items-center gap-2 text-gray-600 mb-6">
            <i className="ri-calendar-line"></i>
            <p className="text-lg">
              <strong>Last Updated:</strong> January 1, 2025
            </p>
          </div>

          <p className="text-xl text-gray-700 leading-relaxed">
            MandaPost, operated by Osem Dynamics LLC, respects your right to privacy. This Privacy Policy explains who we are, how we collect, share, and use information about you, and how you can exercise your privacy rights.
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#e4f4ff]/20 to-[#b3d9ff]/20 rounded-2xl border-2 border-[#66b3ff]/30 p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-white text-2xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#22577A] mb-3">About This Policy</h2>
                <p className="text-lg text-gray-800 leading-relaxed mb-4">
                  This Privacy Policy applies to all users of MandaPost websites and services, including www.mandapost.com and our application platform. For information on the terms which govern your use of our Service, please see our Terms of Service and Subscription Terms.
                </p>
                <p className="text-lg text-gray-800 leading-relaxed">
                  We are committed to protecting your privacy and being transparent about how we collect, use, and share your information. This policy describes our practices in detail and explains your rights under applicable data protection laws.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {sections.map((section, index) => {
              // Highlight important sections
              const isHighlighted = ['13', '14', '24'].includes(section.number);
              const highlightColor = section.number === '24' ? 'from-blue-50 to-blue-100 border-blue-300' : 'from-yellow-50 to-yellow-100 border-yellow-300';

              return (
                <div
                  key={index}
                  className={`rounded-xl border-2 p-8 hover:shadow-lg transition-all ${
                    isHighlighted
                      ? `bg-gradient-to-br ${highlightColor}`
                      : 'bg-white border-gray-200 hover:border-[#66b3ff]'
                  }`}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-xl">{section.number}</span>
                    </div>
                    <h2 className="text-3xl font-bold text-[#22577A] mt-1">{section.title}</h2>
                  </div>
                  <div className="ml-16">
                    {section.content.split('\n\n').map((paragraph, pIndex) => {
                      // Check if paragraph starts with a bullet point or bold heading
                      if (paragraph.startsWith('•')) {
                        return (
                          <p key={pIndex} className="text-gray-700 leading-relaxed mb-3 pl-4">
                            {paragraph}
                          </p>
                        );
                      } else if (paragraph.startsWith('**')) {
                        // Bold headings within sections
                        const parts = paragraph.split('**');
                        return (
                          <p key={pIndex} className="text-gray-700 leading-relaxed mb-4">
                            {parts.map((part, i) => (
                              i % 2 === 1 ? <strong key={i} className="font-semibold text-[#22577A]">{part}</strong> : part
                            ))}
                          </p>
                        );
                      } else {
                        return (
                          <p key={pIndex} className="text-gray-700 leading-relaxed mb-4 last:mb-0">
                            {paragraph}
                          </p>
                        );
                      }
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Contact CTA */}
          <div className="mt-16 bg-gradient-to-r from-[#22577A] to-[#38a3a5] rounded-2xl p-8 text-white">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-customer-service-2-line text-2xl"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold mb-2">Questions About Your Privacy?</h3>
                <p className="text-white/90 mb-6 text-lg">
                  We're committed to protecting your privacy and being transparent about our data practices. If you have any questions or concerns, we're here to help.
                </p>
                <div className="grid sm:grid-cols-3 gap-4">
                  <a
                    href="/contact"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-[#22577A] rounded-lg font-semibold hover:shadow-xl transition-all cursor-pointer"
                  >
                    <i className="ri-mail-line"></i>
                    Contact Us
                  </a>
                  <a
                    href="/terms-of-service"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-white text-white rounded-lg font-semibold hover:bg-white/10 transition-all cursor-pointer"
                  >
                    <i className="ri-file-list-line"></i>
                    Terms of Service
                  </a>
                  <a
                    href="/subscription-terms"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-white text-white rounded-lg font-semibold hover:bg-white/10 transition-all cursor-pointer"
                  >
                    <i className="ri-file-text-line"></i>
                    Subscription Terms
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Related Resources */}
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-[#e4f4ff]/30 to-[#b3d9ff]/30 rounded-xl p-6 border-2 border-[#66b3ff]/30">
              <i className="ri-shield-user-line text-4xl text-[#22577A] mb-3"></i>
              <h3 className="text-xl font-bold text-[#22577A] mb-2">Your Data Rights</h3>
              <p className="text-gray-700 mb-4">
                Learn about your rights to access, correct, delete, and control your personal information under applicable data protection laws.
              </p>
              <a href="#section-13" className="text-[#22577A] font-semibold hover:text-[#38a3a5] transition-colors">
                View Your Rights →
              </a>
            </div>

            <div className="bg-gradient-to-br from-[#e4f4ff]/30 to-[#b3d9ff]/30 rounded-xl p-6 border-2 border-[#66b3ff]/30">
              <i className="ri-lock-password-line text-4xl text-[#22577A] mb-3"></i>
              <h3 className="text-xl font-bold text-[#22577A] mb-2">Security & Protection</h3>
              <p className="text-gray-700 mb-4">
                Discover the technical and organizational measures we implement to protect your information from unauthorized access.
              </p>
              <a href="#section-14" className="text-[#22577A] font-semibold hover:text-[#38a3a5] transition-colors">
                Learn About Security →
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
